import * as React from 'react';
import {useCallback} from 'react';

export interface UseMuiAnchorReturnValue {
  mouseEventEnter: (event: React.MouseEvent<HTMLElement>) => void;
  mouseEventLeave: (event: React.MouseEvent<HTMLElement>) => void;
  open: boolean;
  anchorEl: HTMLElement | null;
  forceCloseTooltip: () => void;
}

type AnchorSetter = React.Dispatch<React.SetStateAction<HTMLElement | null>>;

// At most one tooltip is open at a time, app-wide. Every hover-open registers its own state setter
// here and closes whatever was open before. Without it two tooltips can overlap: `mouseleave` is
// not guaranteed to fire — the browser only recomputes hover on pointer movement, so anything that
// reflows under a still cursor (a popover closing, a dropdown opening) leaves the first tooltip up.
let openTooltipSetter: AnchorSetter | null = null;

// A tooltip must never cover what the user is currently working with. Once the anchor is engaged —
// focused, or holding an open dropdown — hovering it no longer opens the tooltip; it only becomes
// hoverable again after the focus leaves and the dropdown closes. Hover alone (no click yet) is the
// only state that shows a tooltip.
//
// Focus-within alone is not enough: dropdowns that portal their popup (MUI Select/Menu) move focus
// out of the anchor while open, so an expanded trigger inside the anchor counts as engaged too.
// MUI Autocomplete (LightSelectFilter and friends) keeps focus in its input and also flags
// aria-expanded, so both checks catch it.
function isEngaged(element: HTMLElement, ignoreFocus: boolean): boolean {
  const {activeElement} = document;
  if (!ignoreFocus && activeElement && activeElement !== document.body && element.contains(activeElement)) {
    return true;
  }
  return element.matches('[aria-expanded="true"]') || element.querySelector('[aria-expanded="true"]') != null;
}

export type UseMuiAnchorOptions = {
  /** The anchor's focus does not count as engaged - for a tab, which keeps the focus after the click that selected it,
   * so its tooltip would otherwise never show again on the selected tab (PDW-421). */
  ignoreFocus?: boolean;
};

export function useMuiAnchor(hasTooltip: boolean, options?: UseMuiAnchorOptions): UseMuiAnchorReturnValue {
  const ignoreFocus = options?.ignoreFocus ?? false;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const closeTooltip = useCallback(() => {
    if (openTooltipSetter === setAnchorEl) {
      openTooltipSetter = null;
    }
    setAnchorEl(null);
  }, []);

  const mouseEventEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!hasTooltip || isEngaged(event.currentTarget, ignoreFocus)) {
        return;
      }
      if (openTooltipSetter && openTooltipSetter !== setAnchorEl) {
        openTooltipSetter(null);
      }
      openTooltipSetter = setAnchorEl;
      setAnchorEl(event.currentTarget);
    },
    [hasTooltip, ignoreFocus]
  );

  const mouseEventLeave = useCallback(() => {
    if (hasTooltip) {
      closeTooltip();
    }
  }, [hasTooltip, closeTooltip]);

  React.useEffect(() => {
    if (!anchorEl) {
      return;
    }
    const closeOnScroll = () => closeTooltip();
    // Backstop for the missing `mouseleave` described above: any hover landing outside the anchor
    // closes the tooltip, so it cannot stay behind once the pointer has moved on. Hovering a popper
    // is not "outside" — a tooltip placed over its own anchor would otherwise close and reopen in a
    // loop as the pointer alternates between the two.
    const closeOnHoverOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (target && anchorEl.contains(target)) {
        return;
      }
      const targetElement = target instanceof Element ? target : target?.parentElement;
      if (targetElement?.closest('[data-name="dl-mui-popper--popper"]')) {
        return;
      }
      closeTooltip();
    };
    // Focus landing anywhere means the user started interacting — with the anchor (clicked into the
    // field, opened its dropdown) or elsewhere. Either way the tooltip has to go; `isEngaged` then
    // keeps it from reopening on re-hover for as long as the anchor stays focused/expanded.
    const closeOnFocusIn = () => closeTooltip();
    window.addEventListener('scroll', closeOnScroll, true);
    document.addEventListener('mouseover', closeOnHoverOutside, true);
    document.addEventListener('focusin', closeOnFocusIn, true);
    return () => {
      window.removeEventListener('scroll', closeOnScroll, true);
      document.removeEventListener('mouseover', closeOnHoverOutside, true);
      document.removeEventListener('focusin', closeOnFocusIn, true);
    };
  }, [anchorEl, closeTooltip]);

  // Unmounting while open would leave the registry pointing at a setter nobody can call any more.
  React.useEffect(
    () => () => {
      if (openTooltipSetter === setAnchorEl) {
        openTooltipSetter = null;
      }
    },
    []
  );

  const open = Boolean(anchorEl);
  const forceCloseTooltip = useCallback(() => {
    if (hasTooltip) {
      closeTooltip();
    }
  }, [hasTooltip, closeTooltip]);

  return {mouseEventEnter, mouseEventLeave, open, anchorEl, forceCloseTooltip};
}
